/* eslint-disable import/no-extraneous-dependencies */
import {
    ColorSchemeType,
    DARK_THEME_SET_NAME,
    HSL,
    LIGHT_THEME_SET_NAME,
    PrimaryThemeRGBStringColors,
    ThemeHue
} from 'epic-diesel-common/src/models/cms/Theme';

import {
    isValidHex,
    isValidHSL,
    HexToHSL,
    HSLToRGBString,
    HSLAToRGBAString
} from 'epic-diesel-products/src/utils/ColorUtils';

export const GREY_SATURATION_THRESHOLD = 15;

export const TEXT_COLORS = {
    white: {
        h: 0,
        s: 0,
        l: 96
    },
    black: {
        h: 0,
        s: 0,
        l: 7
    }
};

export const DARK_THEME_SETTINGS: Record<string, Record<string, number>> = {
    color: {
        // Card backgrounds
        s: 75,
        l: 15
    },
    bg: {
        // Page backgrounds
        s: 70,
        l: 11
    },
    highlight: {
        // Button and image backgrounds
        s: 75,
        l: 20
    },
    text: TEXT_COLORS.white,
    bodyText: {
        ...TEXT_COLORS.white,
        a: 0.6
    }
};

export const LIGHT_THEME_SETTINGS: Record<string, Record<string, number>> = {
    color: {
        // Card backgrounds
        s: 80,
        l: 92
    },
    bg: {
        // Page backgrounds
        s: 90,
        l: 96
    },
    highlight: {
        // Button and image backgrounds
        s: 80,
        l: 86
    },
    text: TEXT_COLORS.black,
    bodyText: {
        ...TEXT_COLORS.black,
        a: 0.6
    }
};

export const DARK_GREY_THEME_SETTINGS: Record<string, Record<string, number>> = {
    color: {
        // Card backgrounds
        s: 0,
        l: 15
    },
    bg: {
        // Page backgrounds
        s: 0,
        l: 11
    },
    highlight: {
        // Button and image backgrounds
        s: 0,
        l: 20
    },
    text: TEXT_COLORS.white,
    bodyText: {
        ...TEXT_COLORS.white,
        a: 0.6
    }
};

export const LIGHT_GREY_THEME_SETTINGS: Record<string, Record<string, number>> = {
    color: {
        // Card backgrounds
        s: 0,
        l: 92
    },
    bg: {
        // Page backgrounds
        s: 0,
        l: 96
    },
    highlight: {
        // Button and image backgrounds
        s: 0,
        l: 86
    },
    text: TEXT_COLORS.black,
    bodyText: {
        ...TEXT_COLORS.black,
        a: 0.6
    }
};

export const THEME_V2_HUES: ThemeHue = {
    ATLANTIC_BLUE: [228],
    BABY_BLUE: [219],
    BLOOD_RED: [357, 0],
    EPIC_GREY_DARK: [0],
    EPIC_GREY_LIGHT: [0],
    FOREST_GREEN: [186],
    GREEN: [143],
    MAGENTA: [265],
    MAGIC_PURPLE: [263],
    PINK: [355, 0],
    SAND: [40],
    TEAL: [210]
};

const GREY_THEME_NAMES: string[] = ['EPIC_GREY_LIGHT', 'EPIC_GREY_DARK'];

export const numbersAreClose = (a: number, b: number): boolean => Math.abs(a - b) <= 1;

export const anyNumberIsClose = (numbers: number[], test: number): boolean =>
    numbers.some(number => numbersAreClose(number, test));

export const getColorSchemesByThemeSetName = (themeSetNames: string[]): ColorSchemeType[] =>
    themeSetNames.reduce((colorSchemes: ColorSchemeType[], themeSetName: string) => {
        if (LIGHT_THEME_SET_NAME[themeSetName] !== undefined) {
            colorSchemes.push(ColorSchemeType.light);
        }
        if (DARK_THEME_SET_NAME[themeSetName] !== undefined) {
            colorSchemes.push(ColorSchemeType.dark);
        }
        return colorSchemes;
    }, []);

export const getMatchingThemeSetNames = (hsl: HSL): string[] => {
    // Saturation is a more accurate measure than hue for grey themes.
    if (numbersAreClose(hsl.s, 0)) {
        return GREY_THEME_NAMES;
    }

    return Object.keys(THEME_V2_HUES)
        .filter(key => key !== 'EPIC_GREY_DARK' && key !== 'EPIC_GREY_LIGHT')
        .filter(key => anyNumberIsClose(THEME_V2_HUES[key], hsl.h));
};

export const getNearestThemeSetNames = (hsl: HSL, colorScheme: string): string[] => {
    if (!hsl || !isValidHSL(hsl)) return ['EPIC_GREY_DARK'];
    const {h, s} = hsl;
    const isGrey: boolean = s < GREY_SATURATION_THRESHOLD;
    if (isGrey) return colorScheme === 'light' ? ['EPIC_GREY_LIGHT'] : ['EPIC_GREY_DARK'];

    const THEME_SETS = colorScheme === 'light' ? LIGHT_THEME_SET_NAME : DARK_THEME_SET_NAME;

    const tempHues: number[][] = Object.keys(THEME_V2_HUES)
        .filter(key => THEME_SETS[key] !== undefined && !GREY_THEME_NAMES.includes(key))
        .map(key => THEME_V2_HUES[key]);

    const hues: number[] = [].concat.apply([], tempHues);
    const nearestHue: number = hues.sort((a, b) => Math.abs(h - a) - Math.abs(h - b))[0];

    const nearestThemeNames: string[] = Object.keys(THEME_V2_HUES)
        .filter(
            themeName =>
                !GREY_THEME_NAMES.includes(themeName) && THEME_SETS[themeName] !== undefined
        )
        .filter(
            themeName => THEME_V2_HUES[themeName] && THEME_V2_HUES[themeName].includes(nearestHue)
        );

    return nearestThemeNames;
};

export const CMSThemeMatchesV2Theme = (hex: string, colorScheme: string): boolean => {
    if (!hex || !isValidHex(hex)) return false;
    const hsl = HexToHSL(hex);

    const matchingThemeSetNames: string[] = getMatchingThemeSetNames(hsl);
    if (!matchingThemeSetNames.length) return false;

    const matchingThemeSchemes: string[] = getColorSchemesByThemeSetName(matchingThemeSetNames);
    return !!matchingThemeSchemes.length && matchingThemeSchemes.includes(colorScheme);
};

const getHue = (matchingThemeSetNames: string[], colorScheme: string): number => {
    if (colorScheme === 'light grey' || colorScheme === 'dark grey') return 0;
    if (colorScheme === 'light') {
        const theme = matchingThemeSetNames.filter(
            name => LIGHT_THEME_SET_NAME[name] !== undefined
        )[0];
        return THEME_V2_HUES[theme][0];
    }
    const theme = matchingThemeSetNames.filter(name => DARK_THEME_SET_NAME[name] !== undefined)[0];
    return THEME_V2_HUES[theme][0];
};

const applyRules = (
    matchingThemeSetNames: string[],
    settings: Record<string, Record<string, number>>,
    colorScheme: string
): PrimaryThemeRGBStringColors => {
    const h = getHue(matchingThemeSetNames, colorScheme);
    return {
        customPrimaryColor: HSLToRGBString({
            ...settings.color,
            h
        }),
        customPrimaryBg: HSLToRGBString({
            ...settings.bg,
            h
        }),
        customPrimaryBgTransparent: HSLAToRGBAString({
            ...settings.bg,
            h,
            a: 0
        }),
        customPrimaryHighlight: HSLToRGBString({
            ...settings.highlight,
            h
        }),
        customPrimaryText: HSLToRGBString(settings.text),
        customPrimaryBodyText: HSLAToRGBAString(settings.bodyText)
    };
};

const applyThemeRulesToHex = (
    hex: string,
    colorScheme: string,
    isThemeV2: boolean
): PrimaryThemeRGBStringColors => {
    const defaultPrimaryColors: PrimaryThemeRGBStringColors = applyRules(
        ['EPIC_GREY_DARK'],
        DARK_GREY_THEME_SETTINGS,
        'dark grey'
    );
    if (!hex || !isValidHex(hex)) return defaultPrimaryColors;
    const hsl = HexToHSL(hex);
    const matchingThemeSetNames: string[] = isThemeV2
        ? getMatchingThemeSetNames(hsl)
        : getNearestThemeSetNames(hsl, colorScheme);
    if (!matchingThemeSetNames.length) return defaultPrimaryColors;

    const useLightGreyRules: boolean =
        colorScheme === 'light' && matchingThemeSetNames.includes('EPIC_GREY_LIGHT');
    const useDarkGreyRules: boolean =
        colorScheme === 'dark' && matchingThemeSetNames.includes('EPIC_GREY_DARK');
    const useLightRules: boolean =
        colorScheme === 'light' &&
        getColorSchemesByThemeSetName(matchingThemeSetNames).includes(ColorSchemeType.light);
    const useDarkRules: boolean =
        colorScheme === 'dark' &&
        getColorSchemesByThemeSetName(matchingThemeSetNames).includes(ColorSchemeType.dark);

    if (useLightGreyRules) {
        return applyRules(matchingThemeSetNames, LIGHT_GREY_THEME_SETTINGS, 'light grey');
    }
    if (useDarkGreyRules) {
        return applyRules(matchingThemeSetNames, DARK_GREY_THEME_SETTINGS, 'dark grey');
    }
    if (useLightRules) return applyRules(matchingThemeSetNames, LIGHT_THEME_SETTINGS, 'light');
    if (useDarkRules) return applyRules(matchingThemeSetNames, DARK_THEME_SETTINGS, 'dark');

    return defaultPrimaryColors;
};

export const getPrimaryV2ColorsFromCMSTheme = (
    hex: string,
    colorScheme: string
): PrimaryThemeRGBStringColors => {
    const CMSThemeIsV2: boolean = CMSThemeMatchesV2Theme(hex, colorScheme);
    return applyThemeRulesToHex(hex, colorScheme, CMSThemeIsV2);
};
